import { forEach, isNil, omit, compact } from "lodash";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import useSession from "Hooks/session.hook";
import {
  createServicePoint,
  createServicePointType,
  listLines,
  fetchServicePoint,
  fetchServicePointType,
  fetchServicePointTypes,
  updateServicePoint,
  updateServicePointType,
} from "Services/delivery-settings.services";
import { decode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";
import { StackScreen } from "@life-line-lab/components-react";
import {
  ServicePointTypeForm,
  ServicePointForm,
} from "Components/delivery-management/delivery-forms";

const ServicePointsForm: React.FC<any> = () => {
  const history = useHistory();
  const { org } = useSession();
  const { t } = useTranslation();
  const { type } = useParams<{ type: string }>();

  const [settings, setSettings] = useState<any>({});
  const [response, setResponse] = useState<any>([]);
  const [activeForm, setActiveForm] = useState(<></>);

  useEffect(() => {
    if (type) {
      switch (type) {
        case "0":
          setActiveForm(
            <ServicePointTypeForm
              onSubmit={handleCreateServicePointType}
              onUpdate={handleUpdateServicePointType}
              onFetch={handleFetchServicePointType}
              data={response}
            />,
          );
          break;
        case "1":
          setActiveForm(
            <ServicePointForm
              onSubmit={handleCreateServicePoint}
              onUpdate={handleUpdateServicePoint}
              onFetch={handleFetchServicePoint}
              handleFetchLines={handleFetchLines}
              handleFetchPointTypes={handleFetchPointTypes}
              data={response}
              settings={settings}
            />,
          );
          break;
      }
    }
  }, [type, response, settings]);

  const getWorkingHours = (data) => {
    let result: any = [];
    forEach(data.workingHours, (v, k) => {
      if (v.openingTime && v.closingTime)
        result.push({
          day: v.day,
          openingTime: v.openingTime,
          closingTime: v.closingTime,
        });
    });
    return result;
  };

  const handleCreateServicePointType = async (data: any) => {
    data.active = true;
    data.orgID = org._id;

    let responseData = await createServicePointType(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(
        `/delivery-management/settings/service-points?tabKey=${type}`,
      );
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateServicePointType = async (data: any) => {
    data.active = response.active;
    data.orgID = org._id;
    let responseData = await updateServicePointType(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.updateSuccessful"));
      history.push(
        `/delivery-management/settings/service-points?tabKey=${type}`,
      );
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleFetchServicePointType = async (pointTypeID: string) => {
    const responseData = await fetchServicePointType(
      decode(pointTypeID) as string,
    );
    if (!isNil(responseData)) {
      setResponse(responseData);
    }
  };

  const handleCreateServicePoint = async (data: any) => {
    data.active = true;
    data.orgID = org._id;

    let formattedWorkingHours = data?.workingHours?.map((current) => {
      if (current?.isChecked)
        return {
          day: current?.day,
          openingTime: current?.openingTime,
          closingTime: current?.closingTime,
        };
    });

    const formattedData = {
      ...data,
      workingHours: compact(formattedWorkingHours),
    };

    let responseData = await createServicePoint(
      omit(formattedData, ["openingTime", "closingTime"]),
    );

    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(
        `/delivery-management/settings/service-points?tabKey=${type}`,
      );
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateServicePoint = async (data: any) => {
    data.active = response.active;
    data.orgID = org._id;
    data.workingHours = getWorkingHours(data);
    let responseData = await updateServicePoint(
      omit(data, ["openingTime", "closingTime"]),
    );
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.updateSuccessful"));
      history.push(
        `/delivery-management/settings/service-points?tabKey=${type}`,
      );
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleFetchServicePoint = async (pointID: string) => {
    const responseData = await fetchServicePoint(decode(pointID) as string);
    if (!isNil(responseData)) {
      setResponse(responseData);
    }
  };

  const handleFetchLines = async () => {
    const responseData = await listLines();
    setSettings((settings) => {
      return { ...settings, LINE: responseData };
    });
  };

  const handleFetchPointTypes = async () => {
    const responseData = await fetchServicePointTypes();
    setSettings((settings) => {
      return { ...settings, SERVICE_POINT_TYPE: responseData };
    });
  };

  const handleCloseStackScreen = () => {
    history.push(`/delivery-management/settings/service-points?tabKey=${type}`);
  };

  return (
    <StackScreen
      title={
        <div>
          <img
            alt={t("alt.logo")}
            src="/assets/images/banner_logo.png"
            height={30}
          />
        </div>
      }
      isOpen={true}
      renderHeader={<div></div>}
      onClose={handleCloseStackScreen}
    >
      <Helmet>
        <title></title>
      </Helmet>

      {activeForm}
    </StackScreen>
  );
};

export default ServicePointsForm;
